import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * ScrollingListDetector
 * Detects repeated items in vertically scrolling lists across multiple frames
 * 
 * Props:
 *  - urls: string[]                  // signed frame URLs, in order
 *  - results?: {                     // optional backend bundle
 *      ocrResults: Array<{ 
 *        file?: string, 
 *        detections: Array<{ 
 *          text?: string, 
 *          originalText?: string, 
 *          vertices: Array<{x:number,y:number}>,
 *          confidence?: number 
 *        }> 
 *      }>,
 *      maxValues?: { maxTextLength?: number }
 *    }
 *  - ocrResults?: same as results.ocrResults
 */
export default function ScrollingListDetector({ urls = [], results, ocrResults }) {
  const frames = results?.ocrResults || ocrResults || [];

  // -------- Frame selection --------
  const [selectedFrame, setSelectedFrame] = useState(1);
  const [selectedFrameIndex, setSelectedFrameIndex] = useState(1);
  const currentIndex = Math.max(0, Math.min(urls.length - 1, selectedFrame - 1));
  const detections = frames?.[currentIndex]?.detections || [];

  // -------- Template selection --------
  const [templateBox, setTemplateBox] = useState(null); // {x,y,w,h} in normalized coords
  const [templateLocked, setTemplateLocked] = useState(false);
  const [detectedItems, setDetectedItems] = useState([]);

  const [ignoreDuplicates, setIgnoreDuplicates] = useState(true);
  const [templateFrameIndex, setTemplateFrameIndex] = useState(null);

  // -------- Detection parameters --------
  const [verticalTolerance, setVerticalTolerance] = useState(20); // pixels - tolerance for text block positions within item
  const [horizontalTolerance, setHorizontalTolerance] = useState(30); // pixels - tolerance for text block positions within item
  const [minSimilarity, setMinSimilarity] = useState(0.7); // 0-1 similarity threshold
  const [duplicateVerticalTolerance, setDuplicateVerticalTolerance] = useState(0.3);
  const [verticalStepSize, setVerticalStepSize] = useState(0.8);
  const [searchWindowMultiplier, setSearchWindowMultiplier] = useState(1.3);
  const [lineCountTolerance, setLineCountTolerance] = useState(0.3);
  const [spatialMatchWeight, setSpatialMatchWeight] = useState(0.4);
  const [linePositionTolerance, setLinePositionTolerance] = useState(0.15); // 15% tolerance for line Y positions
  const [minDisplaySimilarity, setMinDisplaySimilarity] = useState(0); // Post-detection filter
  const [duplicateSimilarityThreshold, setDuplicateSimilarityThreshold] = useState(0.9); // Items >90% similar = duplicates


  // -------- Image & overlay refs/size --------
  const wrapperRef = useRef(null);
  const imgRef = useRef(null);
  const [overlaySize, setOverlaySize] = useState({ width: 0, height: 0 });
  const [natural, setNatural] = useState({ w: 1, h: 1 });

  // Drag state
  const [drag, setDrag] = useState(null);

  const measure = () => {
    const img = imgRef.current;
    if (!img) return;
    const width = img.clientWidth;
    const height = img.clientHeight;
    setOverlaySize({ width, height });
    setNatural({ w: img.naturalWidth || 1, h: img.naturalHeight || 1 });

    // Initialize template box if none
    if (!templateBox) {
      const w = 0.8;
      const h = 0.15;
      const x = (1 - w) / 2;
      const y = 0.2;
      setTemplateBox({ x, y, w, h });
    }
  };

  useEffect(() => {
    measure();
    const onResize = () => measure();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [urls, selectedFrame]);

  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
  const normToPxLocal = (n) => ({
    left: n.x * overlaySize.width,
    top: n.y * overlaySize.height,
    width: n.w * overlaySize.width,
    height: n.h * overlaySize.height,
  });

  const beginMove = (e) => {
    if (templateLocked || !templateBox || !wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    const selPx = normToPxLocal(templateBox);
    const localX = e.clientX - rect.left;
    const localY = e.clientY - rect.top;
    
    const inside =
      localX >= selPx.left &&
      localX <= selPx.left + selPx.width &&
      localY >= selPx.top &&
      localY <= selPx.top + selPx.height;
    
    if (!inside) return;
    
    const dx = localX - selPx.left;
    const dy = localY - selPx.top;
    
    setDrag({
      type: "move",
      startClient: { x: e.clientX, y: e.clientY },
      startSel: { ...templateBox },
      offset: { dx, dy },
    });
    e.preventDefault();
  };

  const beginResize = (handle, e) => {
    if (templateLocked) return;
    e.stopPropagation();
    setDrag({
      type: "handle",
      handle,
      startClient: { x: e.clientX, y: e.clientY },
      startSel: { ...templateBox },
    });
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!drag || !templateBox || !wrapperRef.current) return;
      const minW = 0.05;
      const minH = 0.05;

      if (drag.type === "move") {
        const rect = wrapperRef.current.getBoundingClientRect();
        const selPx = normToPxLocal(templateBox);
        const newLeftLocal = clamp(
          e.clientX - rect.left - drag.offset.dx,
          0,
          overlaySize.width - selPx.width
        );
        const newTopLocal = clamp(
          e.clientY - rect.top - drag.offset.dy,
          0,
          overlaySize.height - selPx.height
        );
        
        const nn = {
          x: newLeftLocal / overlaySize.width,
          y: newTopLocal / overlaySize.height,
          w: selPx.width / overlaySize.width,
          h: selPx.height / overlaySize.height,
        };
        
        nn.x = clamp(nn.x, 0, 1 - nn.w);
        nn.y = clamp(nn.y, 0, 1 - nn.h);
        setTemplateBox(nn);
      } else if (drag.type === "handle") {
        const absDx = (e.clientX - drag.startClient.x) / overlaySize.width;
        const absDy = (e.clientY - drag.startClient.y) / overlaySize.height;
        
        let { x, y, w, h } = drag.startSel;

        if (drag.handle === "nw") {
          x = clamp(drag.startSel.x + absDx, 0, drag.startSel.x + drag.startSel.w - minW);
          y = clamp(drag.startSel.y + absDy, 0, drag.startSel.y + drag.startSel.h - minH);
          w = clamp(drag.startSel.w - (x - drag.startSel.x), minW, 1 - x);
          h = clamp(drag.startSel.h - (y - drag.startSel.y), minH, 1 - y);
        }
        if (drag.handle === "ne") {
          y = clamp(drag.startSel.y + absDy, 0, drag.startSel.y + drag.startSel.h - minH);
          w = clamp(drag.startSel.w + absDx, minW, 1 - drag.startSel.x);
          h = clamp(drag.startSel.h - (y - drag.startSel.y), minH, 1 - y);
          x = drag.startSel.x;
        }
        if (drag.handle === "se") {
          w = clamp(drag.startSel.w + absDx, minW, 1 - drag.startSel.x);
          h = clamp(drag.startSel.h + absDy, minH, 1 - drag.startSel.y);
          x = drag.startSel.x;
          y = drag.startSel.y;
        }
        if (drag.handle === "sw") {
          x = clamp(drag.startSel.x + absDx, 0, drag.startSel.x + drag.startSel.w - minW);
          w = clamp(drag.startSel.w - (x - drag.startSel.x), minW, 1 - x);
          h = clamp(drag.startSel.h + absDy, minH, 1 - drag.startSel.y);
          y = drag.startSel.y;
        }
        
        setTemplateBox({ x, y, w, h });
      }
    };
    
    const onUp = () => setDrag(null);
    
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [drag, templateBox, overlaySize]);

  // -------- Geometry helpers --------
  function blockBBoxNat(vertices = []) {
    const xs = vertices.map((p) => p.x);
    const ys = vertices.map((p) => p.y);
    if (!xs.length || !ys.length) return { x: 0, y: 0, w: 0, h: 0 };
    const minX = Math.min(...xs), maxX = Math.max(...xs);
    const minY = Math.min(...ys), maxY = Math.max(...ys);
    return { x: minX, y: minY, w: Math.max(0, maxX - minX), h: Math.max(0, maxY - minY) };
  }

  const insideSel = (bbNat, sel) => {
    if (!sel) return false;
    const b = {
      x: bbNat.x / natural.w,
      y: bbNat.y / natural.h,
      w: bbNat.w / natural.w,
      h: bbNat.h / natural.h,
    };
    return (
      b.x >= sel.x &&
      b.y >= sel.y &&
      b.x + b.w <= sel.x + sel.w &&
      b.y + b.h <= sel.y + sel.h
    );
  };

  // -------- Raw text block outlines --------
  const textBlockOutlines = useMemo(() => {
    const bufferPx = 4; // 4 pixel buffer for text blocks
    return (detections || []).map((d, i) => {
      const bbN = blockBBoxNat(d.vertices || []);
      let left = (bbN.x / natural.w) * overlaySize.width;
      let top = (bbN.y / natural.h) * overlaySize.height;
      let width = (bbN.w / natural.w) * overlaySize.width;
      let height = (bbN.h / natural.h) * overlaySize.height;
      
      // Apply buffer
      left = Math.max(0, left - bufferPx);
      top = Math.max(0, top - bufferPx);
      width = Math.min(overlaySize.width - left, width + 2 * bufferPx);
      height = Math.min(overlaySize.height - top, height + 2 * bufferPx);
      
      return { key: i, left, top, width, height };
    });
  }, [detections, overlaySize, natural]);

  // -------- Template Analysis --------
const templatePattern = useMemo(() => {
  if (!templateBox || !templateLocked || templateFrameIndex === null) return null;
  
  const templateDets = frames?.[templateFrameIndex]?.detections || [];
  const templateBlocks = templateDets.filter(d => {
    const bb = blockBBoxNat(d.vertices || []);
    return insideSel(bb, templateBox);
  });

  if (templateBlocks.length === 0) return null;

  // Get bounding box of all blocks in template (for relative positioning only)
  const allVertices = templateBlocks.flatMap(d => d.vertices || []);
  const textBounds = blockBBoxNat(allVertices);
  
  // USE THE ACTUAL TEMPLATE BOX DIMENSIONS, not text bounding box
  const templateBounds = {
    x: templateBox.x * natural.w,
    y: templateBox.y * natural.h,
    w: templateBox.w * natural.w,
    h: templateBox.h * natural.h
  };
  
  // Process blocks into lines with position info
  const blocksWithPos = templateBlocks.map(d => {
    const bb = blockBBoxNat(d.vertices || []);
    return {
      text: (d.text || d.originalText || "").trim(),
      x: bb.x,
      y: bb.y,
      w: bb.w,
      h: bb.h,
      centerY: bb.y + bb.h / 2
    };
  });
  
  // ... rest of the grouping logic stays the same ...
  // Sort by Y position
  blocksWithPos.sort((a, b) => a.centerY - b.centerY);
  
  // Group into horizontal lines
  const lines = [];
  let currentLine = [];
  
  blocksWithPos.forEach(block => {
    if (currentLine.length === 0) {
      currentLine.push(block);
    } else {
      const lastBlock = currentLine[currentLine.length - 1];
      const yDiff = Math.abs(block.centerY - lastBlock.centerY);
      const avgHeight = (block.h + lastBlock.h) / 2;
      
      if (yDiff < avgHeight * 0.6) {
        currentLine.push(block);
      } else {
        currentLine.sort((a, b) => a.x - b.x);
        lines.push(currentLine);
        currentLine = [block];
      }
    }
  });
  
  if (currentLine.length > 0) {
    currentLine.sort((a, b) => a.x - b.x);
    lines.push(currentLine);
  }
  
  // Combine blocks within each line based on horizontal spacing
  const processedLines = lines.map(line => {
    const combined = [];
    let current = null;
    
    line.forEach(block => {
      if (!current) {
        current = { ...block, blocks: [block] };
      } else {
        const gap = block.x - (current.x + current.w);
        const avgWidth = (current.w + block.w) / 2;
        
        if (gap < avgWidth * 0.5) {
          current.text = current.text + " " + block.text;
          current.w = (block.x + block.w) - current.x;
          current.blocks.push(block);
        } else {
          combined.push(current);
          current = { ...block, blocks: [block] };
        }
      }
    });
    
    if (current) combined.push(current);
    return combined;
  });
  
  // Identify sections based on vertical spacing between lines
  const sections = [];
  let currentSection = [];
  
  processedLines.forEach((line, idx) => {
    if (currentSection.length === 0) {
      currentSection.push(line);
    } else {
      const prevLine = processedLines[idx - 1];
      const prevMaxY = Math.max(...prevLine.map(seg => seg.y + seg.h));
      const currentMinY = Math.min(...line.map(seg => seg.y));
      const gap = currentMinY - prevMaxY;
      
      const allSegs = currentSection.flat();
      const avgHeight = allSegs.reduce((sum, seg) => sum + seg.h, 0) / allSegs.length;
      
      if (gap > avgHeight * 0.8) {
        sections.push(currentSection);
        currentSection = [line];
      } else {
        currentSection.push(line);
      }
    }
  });
  
  if (currentSection.length > 0) {
    sections.push(currentSection);
  }
  
  return {
    width: templateBounds.w,    // Use template box width
    height: templateBounds.h,   // Use template box height
    bounds: templateBounds,     // Use template box bounds
    sections: sections.map(section => ({
      lines: section.map(line => ({
        segments: line.map(seg => ({
          text: seg.text,
          relX: (seg.x - templateBounds.x) / templateBounds.w,
          relY: (seg.y - templateBounds.y) / templateBounds.h,
          relW: seg.w / templateBounds.w,
          relH: seg.h / templateBounds.h
        }))
      })),
      lineCount: section.length
    })),
    totalLines: processedLines.length,
    sectionCount: sections.length,
    frameIndex: templateFrameIndex
  };
}, [templateBox, templateLocked, frames, templateFrameIndex, natural]);

  // -------- Pattern Detection --------

  function createSpatialFingerprint(blocks, boundsX, boundsY, boundsWidth, boundsHeight) {
    const gridRows = 5;
    const gridCols = 5;
    const grid = Array(gridRows).fill(0).map(() => Array(gridCols).fill(0));
    
    blocks.forEach(block => {
      const relX = (block.x - boundsX) / boundsWidth;
      const relY = (block.y - boundsY) / boundsHeight;
      
      const gridX = Math.max(0, Math.min(gridCols - 1, Math.floor(relX * gridCols)));
      const gridY = Math.max(0, Math.min(gridRows - 1, Math.floor(relY * gridRows)));
      
      grid[gridY][gridX] += 1;
    });
    
    const max = Math.max(...grid.flat(), 1);
    return grid.map(row => row.map(val => val / max));
  }

  function compareSpatialFingerprints(fp1, fp2) {
    let totalDiff = 0;
    let cells = 0;
    
    for (let i = 0; i < fp1.length; i++) {
      for (let j = 0; j < fp1[i].length; j++) {
        totalDiff += Math.abs(fp1[i][j] - fp2[i][j]);
        cells++;
      }
    }
    
    return 1 - (totalDiff / cells);
  }

  // -------- Pattern Detection --------
  const detectPattern = () => {
    if (!templatePattern) {
      console.log("No template pattern");
      return;
    }
    
    console.log("Template:", {
      lines: templatePattern.totalLines,
      sections: templatePattern.sectionCount,
      height: templatePattern.height,
      width: templatePattern.width
    });
    
    // Calculate minimum line spacing in template (our scan increment)
    const templateLineYPositions = templatePattern.sections.flatMap(section =>
      section.lines.map(line => line.segments[0].relY * templatePattern.height)
    ).sort((a, b) => a - b);
    
    const lineSpacings = [];
    for (let i = 0; i < templateLineYPositions.length - 1; i++) {
      lineSpacings.push(templateLineYPositions[i + 1] - templateLineYPositions[i]);
    }
    const minLineSpacing = Math.min(...lineSpacings);
    const scanIncrement = Math.max(5, Math.floor(minLineSpacing * 0.3)); // Half of min spacing for precision
    
    console.log("Min line spacing:", minLineSpacing.toFixed(1), "Scan increment:", scanIncrement);
    
    // Create template signature: expected Y positions of lines (relative, 0-1)
    const templateSignature = templatePattern.sections.flatMap(section =>
      section.lines.map(line => ({
        relY: line.segments[0].relY,
        segmentCount: line.segments.length
      }))
    );
    
    console.log("Template signature:", templateSignature.map(s => `Y=${s.relY.toFixed(2)} segs=${s.segmentCount}`));
    
    const allDetected = [];
    const itemHeight = templatePattern.height;
    const itemWidth = templatePattern.width;
    const templateLeft = templatePattern.bounds.x;
    
    frames.forEach((frame, frameIdx) => {
      const frameDets = frame?.detections || [];
      if (!frameDets.length) return;
      
      console.log(`\nFrame ${frameIdx + 1}: ${frameDets.length} detections`);
      
      // Convert to blocks
      const allBlocks = frameDets.map(d => {
        const bb = blockBBoxNat(d.vertices || []);
        return {
          text: (d.text || d.originalText || "").trim(),
          x: bb.x,
          y: bb.y,
          w: bb.w,
          h: bb.h,
          centerY: bb.y + bb.h / 2
        };
      });
      
      // Filter to template horizontal bounds
      const relevantBlocks = allBlocks.filter(b => {
        const blockCenterX = b.x + b.w / 2;
        return (
          blockCenterX >= templateLeft - itemWidth * 0.1 &&
          blockCenterX <= templateLeft + itemWidth + itemWidth * 0.1
        );
      });
      
      if (relevantBlocks.length < 2) return;
      
      const minY = Math.min(...relevantBlocks.map(b => b.y));
      const maxY = Math.max(...relevantBlocks.map(b => b.y + b.h));
      
      // Track occupied ranges
      const occupiedRanges = [];
      
      // Scan line-by-line through the frame
      for (let scanY = minY; scanY <= maxY - itemHeight * 0.5; scanY += scanIncrement) {
        // Check for overlap
        const wouldOverlap = occupiedRanges.some(range => {
          const overlapStart = Math.max(scanY, range.start);
          const overlapEnd = Math.min(scanY + itemHeight, range.end);
          const overlapAmount = overlapEnd - overlapStart;
          return overlapAmount > .1;
        });
        
        if (wouldOverlap) continue;

        const templateTopBlock = templatePattern.sections[0].lines[0].segments[0];
        const templateTopBlockRelY = templateTopBlock.relY; // How far from top of template box

        
        // Helper function to evaluate a position
        const evaluatePosition = (posY) => {
          const itemWindow = {
            x: templateLeft,
            y: posY,
            w: itemWidth,
            h: itemHeight
          };
          
          const blocksInWindow = relevantBlocks.filter(b => {
            return (
              b.centerY >= itemWindow.y &&
              b.centerY <= itemWindow.y + itemWindow.h
            );
          });
          
          if (blocksInWindow.length < 2) return null;
                  
          // Group into lines
          const sortedBlocks = [...blocksInWindow].sort((a, b) => a.centerY - b.centerY);
          const lines = [];
          let currentLine = [sortedBlocks[0]];
          
          for (let i = 1; i < sortedBlocks.length; i++) {
            const block = sortedBlocks[i];
            const lastBlock = currentLine[currentLine.length - 1];
            const yDiff = Math.abs(block.centerY - lastBlock.centerY);
            const avgHeight = (block.h + lastBlock.h) / 2;
            
            if (yDiff < avgHeight * 0.7) {
              currentLine.push(block);
            } else {
              currentLine.sort((a, b) => a.x - b.x);
              lines.push(currentLine);
              currentLine = [block];
            }
          }
          
          if (currentLine.length > 0) {
            currentLine.sort((a, b) => a.x - b.x);
            lines.push(currentLine);
          }
          
          // Check line count
          if (lines.length < templatePattern.totalLines * 0.7 || 
              lines.length > templatePattern.totalLines * 1.3) {
            return null;
          }
          
          // Create signature
          const candidateSignature = lines.map(line => {
            const lineY = Math.min(...line.map(b => b.y));
            return {
              relY: (lineY - posY) / itemHeight,
              segmentCount: line.length
            };
          });
          
          // Match signatures
          let signatureMatchScore = 0;
          const matchedLines = Math.min(candidateSignature.length, templateSignature.length);
          
          for (let i = 0; i < matchedLines; i++) {
            const candY = candidateSignature[i].relY;
            const tempY = templateSignature[i].relY;
            const yDiff = Math.abs(candY - tempY);
            
            if (yDiff < linePositionTolerance) {
              signatureMatchScore += 1;
            } else if (yDiff < linePositionTolerance * 2) {
              signatureMatchScore += 0.5;
            }
          }
          
          signatureMatchScore = matchedLines > 0 ? signatureMatchScore / matchedLines : 0;
          
          if (signatureMatchScore < 0.7) return null;
          
          // Combine text within lines
          const processedLines = lines.map(line => {
            const combined = [];
            let current = null;
            
            line.forEach(block => {
              if (!current) {
                current = { ...block };
              } else {
                const gap = block.x - (current.x + current.w);
                const avgWidth = (current.w + block.w) / 2;
                
                if (gap < avgWidth * 0.6) {
                  current.text = current.text + " " + block.text;
                  current.w = (block.x + block.w) - current.x;
                } else {
                  combined.push(current);
                  current = { ...block };
                }
              }
            });
            
            if (current) combined.push(current);
            return combined;
          });
          
          // Scoring
          const lineDiff = Math.abs(lines.length - templatePattern.totalLines);
          const lineScore = Math.max(0, 1 - (lineDiff / templatePattern.totalLines));
          
          const templateSegmentCounts = templatePattern.sections.flatMap(s => 
            s.lines.map(l => l.segments.length)
          );
          const candidateSegmentCounts = processedLines.map(line => line.length);
          
          let segmentScore = 0;
          const minLen = Math.min(templateSegmentCounts.length, candidateSegmentCounts.length);
          if (minLen > 0) {
            for (let k = 0; k < minLen; k++) {
              const diff = Math.abs(templateSegmentCounts[k] - candidateSegmentCounts[k]);
              segmentScore += Math.max(0, 1 - (diff / Math.max(templateSegmentCounts[k], 1)));
            }
            segmentScore = segmentScore / minLen;
          }
          
          const similarity = (
            signatureMatchScore * 0.5 +
            lineScore * 0.3 +
            segmentScore * 0.2
          );
          
          if (similarity < minSimilarity) return null;
          
          // Find the top-most text block in this candidate
          const topBlockY = Math.min(...blocksInWindow.map(b => b.y));
          
          // Calculate where the item box SHOULD start based on template's top block position
          const adjustedItemY = topBlockY - (templateTopBlockRelY * itemHeight);
          
          const itemTexts = processedLines.map(line => 
            line.map(seg => seg.text).join(" ")
          );
          
          return {
            y: adjustedItemY,  // Use adjusted position, not scan position
            scanY: posY,       // Keep track of where we scanned from
            window: {
              x: templateLeft,
              y: adjustedItemY,
              w: itemWidth,
              h: itemHeight
            },
            texts: itemTexts,
            similarity: similarity,
            signatureScore: signatureMatchScore,
            lines: lines.length,
            key: itemTexts.join("|")
          };
        };
        
        // Evaluate current position and next two positions
        const candidates = [
          evaluatePosition(scanY),
          evaluatePosition(scanY + scanIncrement),
          evaluatePosition(scanY + 2 * scanIncrement)
        ].filter(c => c !== null);
        
        if (candidates.length > 0) {
          // Choose the best match among the three positions
          const bestMatch = candidates.reduce((best, current) => 
            current.similarity > best.similarity ? current : best
          );
          
          // Check if already found nearby
          const alreadyFound = allDetected.some(existing => 
            existing.frameIndex === frameIdx &&
            Math.abs(existing.bounds.y - bestMatch.y) < itemHeight * 0.2 &&
            existing.key === bestMatch.key
          );
          
          if (!alreadyFound) {
            console.log(`  ✓ Y=${bestMatch.y.toFixed(0)}, ${bestMatch.lines} lines, sig=${bestMatch.signatureScore.toFixed(2)}, sim=${bestMatch.similarity.toFixed(2)}`);
            
            allDetected.push({
              frameIndex: frameIdx,
              frame: frameIdx + 1,
              bounds: bestMatch.window,
              texts: bestMatch.texts,
              similarity: bestMatch.similarity,
              lines: bestMatch.lines,
              key: bestMatch.key
            });
            
            occupiedRanges.push({
              start: bestMatch.y,
              end: bestMatch.y + itemHeight
            });
            
            // Jump ahead from the best match position
            scanY = bestMatch.y + itemHeight * 0.7;
          }
        }
      }
    });
    
    console.log(`\nTotal detected: ${allDetected.length} items`);
    
    setDetectedItems(allDetected);
    
  };

function calculateTextSimilarity(texts1, texts2) {
  const text1 = texts1.join(" ").toLowerCase();
  const text2 = texts2.join(" ").toLowerCase();
  
  const words1 = new Set(text1.split(/\s+/).filter(w => w.length > 0));
  const words2 = new Set(text2.split(/\s+/).filter(w => w.length > 0));
  
  const intersection = new Set([...words1].filter(x => words2.has(x)));
  const union = new Set([...words1, ...words2]);
  
  return union.size > 0 ? intersection.size / union.size : 0;
}

const uniqueItems = useMemo(() => {
  if (detectedItems.length === 0) return [];
  
  const uniqueMap = new Map();
  let uniqueIndex = 1;
  
  detectedItems.forEach(item => {
    let foundMatch = false;
    
    for (const [key, existingItem] of uniqueMap.entries()) {
      const similarity = calculateTextSimilarity(item.texts, existingItem.texts);
      
      if (similarity >= duplicateSimilarityThreshold) {
        // This is a duplicate - add to occurrences
        existingItem.occurrences.push(item.frameIndex);
        
        // Keep the one with higher template match score
        if (item.similarity > existingItem.similarity) {
          uniqueMap.set(key, { 
            ...item, 
            uniqueId: existingItem.uniqueId, 
            occurrences: existingItem.occurrences 
          });
        }
        
        foundMatch = true;
        break;
      }
    }
    
    if (!foundMatch) {
      const newKey = `unique_${uniqueIndex}`;
      uniqueMap.set(newKey, {
        ...item,
        uniqueId: uniqueIndex++,
        occurrences: [item.frameIndex]
      });
    }
  });
  
  return Array.from(uniqueMap.values());
}, [detectedItems, duplicateSimilarityThreshold]);

  // Get items to display on current frame
  const currentFrameDisplayItems = useMemo(() => {
    const frameItems = detectedItems.filter(item => 
      item.frameIndex === currentIndex && 
      item.similarity >= minDisplaySimilarity
    );
    
    if (ignoreDuplicates && uniqueItems.length > 0) {
      return frameItems.map(item => {
        const uniqueItem = uniqueItems.find(ui => ui.key === item.key);
        return {
          ...item,
          displayId: uniqueItem ? uniqueItem.uniqueId : null
        };
      });
    }
    
    return frameItems.map((item, idx) => ({
      ...item,
      displayId: idx + 1
    }));
  }, [detectedItems, uniqueItems, currentIndex, ignoreDuplicates, minDisplaySimilarity]);


  // -------- Export functions --------
  function escapeCsvValue(val) {
    if (val === null || val === undefined) return "";
    let s = String(val);
    if (/^[=+\-@]/.test(s)) s = "'" + s;
    const mustQuote = /[",\r\n]/.test(s) || /^\s|\s$/.test(s);
    if (mustQuote) s = '"' + s.replace(/"/g, '""') + '"';
    return s;
  }
  
  function rowsToCsv(rows) {
    const lines = rows.map((r) => r.map(escapeCsvValue).join(",")).join("\r\n");
    return "\uFEFF" + lines;
  }

  function exportCSV() {
    const items = ignoreDuplicates ? uniqueItems : detectedItems;
    if (!items.length) return;
    
    // Determine max number of text fields
    const maxFields = Math.max(...items.map(item => item.texts.length));
    
    const headers = ["Frame"];
    for (let i = 0; i < maxFields; i++) {
      headers.push(`Field ${i + 1}`);
    }
    if (!ignoreDuplicates) headers.push("Similarity");
    
    const rows = [headers];
    
    items.forEach(item => {
      const row = [String(item.frame)];
      item.texts.forEach(text => row.push(text));
      // Pad with empty values if needed
      while (row.length < headers.length - (!ignoreDuplicates ? 1 : 0)) {
        row.push("");
      }
      if (!ignoreDuplicates) row.push(item.similarity.toFixed(2));
      rows.push(row);
    });
    
    const csv = rowsToCsv(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `list_items_${ignoreDuplicates ? 'unique' : 'all'}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  function exportTXT() {
    const items = ignoreDuplicates ? uniqueItems : detectedItems;
    
    if (!items.length) return;
    
    let output = [];
    
    // Process each item
    items.forEach((item, itemIdx) => {
      // Get the original detections for this item to preserve layout
      const itemDets = frames[item.frameIndex]?.detections || [];
      const itemBlocks = [];
      
      // Find the text blocks that belong to this item based on bounds
      itemDets.forEach(d => {
        const bb = blockBBoxNat(d.vertices || []);
        // Check if this detection is within the item bounds
        if (bb.x >= item.bounds.x && 
            bb.y >= item.bounds.y && 
            bb.x + bb.w <= item.bounds.x + item.bounds.w &&
            bb.y + bb.h <= item.bounds.y + item.bounds.h) {
          itemBlocks.push({
            text: (d.text || d.originalText || "").trim(),
            x: bb.x - item.bounds.x, // relative to item
            y: bb.y - item.bounds.y, // relative to item
            w: bb.w,
            h: bb.h
          });
        }
      });
      
      // Sort blocks by Y position, then X position
      itemBlocks.sort((a, b) => {
        const yDiff = a.y - b.y;
        if (Math.abs(yDiff) < 10) { // Same line threshold
          return a.x - b.x;
        }
        return yDiff;
      });
      
      // Group blocks into lines
      const lines = [];
      let currentLine = [];
      let lastY = null;
      
      itemBlocks.forEach(block => {
        if (lastY === null || Math.abs(block.y - lastY) < 10) {
          // Same line
          currentLine.push(block);
          lastY = block.y;
        } else {
          // New line
          if (currentLine.length > 0) {
            lines.push(currentLine);
          }
          currentLine = [block];
          lastY = block.y;
        }
      });
      
      if (currentLine.length > 0) {
        lines.push(currentLine);
      }
      
      // Build item text with spatial layout
      // output.push(`Item ${ignoreDuplicates ? item.uniqueId : itemIdx + 1} (Frame ${item.frame}):`);
      // output.push('-'.repeat(40));
      
      lines.forEach(line => {
        // Sort blocks in line by X position
        line.sort((a, b) => a.x - b.x);
        
        // Build line with spacing
        let lineText = '';
        let lastEndX = 0;
        
        line.forEach(block => {
          // Calculate spacing based on horizontal position
          const spaces = Math.max(1, Math.round((block.x - lastEndX) / 20)); // Adjust divisor for spacing
          lineText += ' '.repeat(spaces) + block.text;
          lastEndX = block.x + block.w;
        });
        
        output.push(lineText.trim());
      });
      
      output.push(''); // Blank line between items
    });
    
    // Add statistics at the end
    output.push('');
    output.push('='.repeat(50));
    output.push('STATISTICS');
    output.push('='.repeat(50));
    output.push(`Total frames processed: ${frames.length}`);
    output.push(`Items detected: ${detectedItems.length}`);
    output.push(`Unique items: ${uniqueItems.length}`);
    output.push(`Duplicates found: ${detectedItems.length - uniqueItems.length}`);
    
    const txtContent = output.join('\n');
    const blob = new Blob([txtContent], { type: 'text/plain;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `list_items_${ignoreDuplicates ? 'unique' : 'all'}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  // -------- Frame navigation --------
  useEffect(() => {
    setSelectedFrameIndex(selectedFrame);
  }, [selectedFrame]);

  useEffect(() => {
    const total = Math.max(1, urls.length);
    setSelectedFrame((f) => Math.min(Math.max(1, f), total));
    setSelectedFrameIndex((i) => Math.min(Math.max(1, i), total));
  }, [urls.length]);

  function handleFrameSelectionChange({ kind, delta = 0, value }) {
    const total = Math.max(1, urls.length);
    const clamp = (v) => Math.min(Math.max(1, v), total);
    const commitTo = (v) => {
      const next = clamp(Number(v));
      setSelectedFrame(next);
      setSelectedFrameIndex(next);
    };

    if (total < 25) {
      if (kind === "button") {
        commitTo(selectedFrame + delta);
      } else {
        commitTo(value != null ? value : selectedFrameIndex);
      }
      return;
    }

    if (kind === "button") {
      commitTo(selectedFrame + delta);
      return;
    }

    if (kind === "change") {
      setSelectedFrameIndex(clamp(Number(value)));
      return;
    }

    if (kind === "commit") {
      commitTo(value != null ? value : selectedFrameIndex);
      return;
    }
  }

  // Get items for list display
  const listDisplayItems = useMemo(() => {
    const items = ignoreDuplicates ? uniqueItems : detectedItems;
    return items.filter(item => item.similarity >= minDisplaySimilarity);
  }, [detectedItems, uniqueItems, ignoreDuplicates, minDisplaySimilarity]);


  // -------- Render --------
  const templatePx = templateBox ? normToPxLocal(templateBox) : null;

  return (
    <div className="w-full max-w-7xl mx-auto p-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Left: image + overlay */}
      <div className="lg:col-span-2">
        <div className="relative w-full max-h-[60vh] rounded-xl border overflow-hidden">
          <div ref={wrapperRef} className="relative inline-block mx-auto">
            {urls.length ? (
              <img
                ref={imgRef}
                src={urls[currentIndex]}
                alt={`frame ${currentIndex + 1}`}
                className="block max-w-full max-h-[60vh] object-contain select-none"
                onLoad={measure}
                draggable={false}
              />
            ) : (
              <div className="h-[60vh] flex items-center justify-center">No image</div>
            )}

            {/* Raw text block boundaries */}
            {textBlockOutlines.map((o) => (
              <div
                key={o.key}
                className="absolute rounded border border-gray-400"
                style={{
                  left: o.left,
                  top: o.top,
                  width: o.width,
                  height: o.height,
                  pointerEvents: 'none',
                }}
              />
            ))}

            {/* Template box */}
            {overlaySize.width > 0 && overlaySize.height > 0 && templatePx && (
              <div className="absolute inset-0">
                {(!templateLocked || (templateLocked && templateFrameIndex === currentIndex)) && 
                <div
                  className={`absolute border-2 ${
                    templateLocked 
                      ? 'border-blue-600 bg-blue-500/10' 
                      : 'border-purple-500 bg-purple-500/5'
                  } rounded ${templateLocked ? '' : 'cursor-move'}`}
                  style={{
                    left: templatePx.left,
                    top: templatePx.top,
                    width: templatePx.width,
                    height: templatePx.height,
                  }}
                  onMouseDown={beginMove}
                  title={templateLocked 
                    ? "Template locked - click Unlock to modify" 
                    : "Drag to position template, use corners to resize"}
                >
                  {!templateLocked && (
                    <>
                      {renderHandle("nw", beginResize)}
                      {renderHandle("ne", beginResize)}
                      {renderHandle("se", beginResize)}
                      {renderHandle("sw", beginResize)}
                    </>
                  )}
                  
                  <div className={`absolute -top-6 left-0 text-xs font-bold ${
                    templateLocked ? 'text-blue-600' : 'text-purple-600'
                  } bg-white px-1 rounded`}>
                    {templateLocked ? 'Template (Locked)' : 'Template - Define One Item'}
                  </div>
                </div>
            }

                {/* Detected items on current frame (always show all) */}
                {currentFrameDisplayItems.map((item, idx) => {
                  const bufferPx = 4; // 4 pixel buffer
                  const itemPx = {
                    left: Math.max(0, (item.bounds.x / natural.w) * overlaySize.width - bufferPx),
                    top: Math.max(0, (item.bounds.y / natural.h) * overlaySize.height - bufferPx),
                    width: Math.min(
                      overlaySize.width - ((item.bounds.x / natural.w) * overlaySize.width - bufferPx),
                      (item.bounds.w / natural.w) * overlaySize.width + 2 * bufferPx
                    ),
                    height: Math.min(
                      overlaySize.height - ((item.bounds.y / natural.h) * overlaySize.height - bufferPx),
                      (item.bounds.h / natural.h) * overlaySize.height + 2 * bufferPx
                    ),
                  };
                  
                  return (
                    <div
                      key={idx}
                      className="absolute border-2 border-green-500 bg-green-500/10 rounded pointer-events-none"
                      style={itemPx}
                    >
                      <div className="absolute -top-5 left-0 text-xs font-bold text-green-600 bg-white px-1 rounded">
                        Item {item.displayId}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Controls */}
        <div className="flex flex-col md:flex-row w-full gap-2 md:gap-4 mt-3">
          {/* Frame slider */}
          <div className="flex flex-grow items-center gap-3">
            <button
              className="btn btn-sm"
              onClick={() => handleFrameSelectionChange({ kind: "button", delta: -1 })}
            >
              Prev
            </button>

            <input
              type="range"
              className="range range-primary"
              min={1}
              max={Math.max(1, urls.length)}
              value={selectedFrameIndex}
              onChange={(e) =>
                handleFrameSelectionChange({ kind: "change", value: e.target.value })
              }
              onMouseUp={() => handleFrameSelectionChange({ kind: "commit" })}
              onTouchEnd={() => handleFrameSelectionChange({ kind: "commit" })}
              onBlur={() => handleFrameSelectionChange({ kind: "commit" })}
            />

            <button
              className="btn btn-sm"
              onClick={() => handleFrameSelectionChange({ kind: "button", delta: +1 })}
            >
              Next
            </button>

            <div className="text-sm opacity-70">Frame {selectedFrameIndex} / {urls.length || 0}</div>
          </div>
        </div>

        {/* Template controls */}
        <div className="mt-3 p-3 rounded-xl border">
          <div className="font-medium mb-2">Define Template</div>
          <div className="text-sm opacity-70 mb-3">
            Position the purple box around ONE complete list item (e.g., one contact, one product)
          </div>
          <div className="flex gap-2">
            <button
              className={`btn btn-sm ${templateLocked ? 'btn-warning' : 'btn-primary'}`}
              onClick={() => {
                if (!templateLocked) {
                  // Locking the template - capture current frame
                  setTemplateFrameIndex(currentIndex);
                  setTemplateLocked(true);
                } else {
                  // Unlocking - clear the frame index
                  setTemplateFrameIndex(null);
                  setTemplateLocked(false);
                }
                setDetectedItems([]);
              }}
            >
              {templateLocked ? 'Unlock Template' : 'Lock Template'}
            </button>
            
            {templateLocked && (
              <>
                <button
                  className="btn btn-success btn-sm"
                  onClick={detectPattern}
                >
                  Detect Items
                </button>
                {detectedItems.length > 0 && 
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    setDetectedItems([]);
                  }}
                >
                  Clear Results
                </button>}
              </>
            )}
          </div>
          
        {templatePattern && (
          <div className="mt-2 text-sm">
            Template: <b>{templatePattern.totalLines}</b> lines, <b>{templatePattern.sectionCount}</b> sections
          </div>
        )}
        </div>

        {/* Detection parameters */}
        {/* <div className="mt-3 p-3 rounded-xl border">
          <div className="font-medium mb-2">Detection Parameters</div>
          
          <label className="label">
            <span className="label-text tooltip tooltip-top" 
                  data-tip="How much vertical variation to allow for text block positions within each item">
              Vertical Tolerance (text blocks): <b>{verticalTolerance}px</b>
            </span>
          </label>
          <input
            type="range"
            className="range range-xs"
            min={5}
            max={50}
            value={verticalTolerance}
            onChange={(e) => setVerticalTolerance(Number(e.target.value))}
          />
          
          <label className="label mt-2">
            <span className="label-text tooltip tooltip-top"
                  data-tip="How much horizontal variation to allow for text block positions within each item">
              Horizontal Tolerance (text blocks): <b>{horizontalTolerance}px</b>
            </span>
          </label>
          <input
            type="range"
            className="range range-xs"
            min={5}
            max={100}
            value={horizontalTolerance}
            onChange={(e) => setHorizontalTolerance(Number(e.target.value))}
          />
          
          <label className="label mt-2">
            <span className="label-text">
              Minimum Similarity: <b>{(minSimilarity * 100).toFixed(0)}%</b>
            </span>
          </label>
          <input
            type="range"
            className="range range-xs"
            min={0.3}
            max={1}
            step={0.05}
            value={minSimilarity}
            onChange={(e) => setMinSimilarity(Number(e.target.value))}
          />
        </div> */}
  {/* Detection parameters */}
  <div className="mt-3 p-3 rounded-xl border">
    <div className="font-medium mb-2">Detection Parameters</div>
    <div className="text-sm opacity-70 mb-3">Controls how items are identified during scanning</div>
    
    <label className="label">
      <span className="label-text tooltip tooltip-top"
            data-tip="How closely line Y positions must match template. Lower = stricter positioning.">
        Line Position Tolerance: <b>{(linePositionTolerance * 100).toFixed(0)}%</b>
      </span>
    </label>
    <input
      type="range"
      className="range range-xs"
      min={0.01}
      max={0.3}
      step={0.01}
      value={linePositionTolerance}
      onChange={(e) => setLinePositionTolerance(Number(e.target.value))}
    />
    
    <label className="label mt-2">
      <span className="label-text tooltip tooltip-top"
            data-tip="How much variation in line count to allow (±%). Lower = stricter matching.">
        Line Count Tolerance: <b>±{(lineCountTolerance * 100).toFixed(0)}%</b>
      </span>
    </label>
    <input
      type="range"
      className="range range-xs"
      min={0.1}
      max={0.5}
      step={0.05}
      value={lineCountTolerance}
      onChange={(e) => setLineCountTolerance(Number(e.target.value))}
    />
    
    <label className="label mt-2">
      <span className="label-text tooltip tooltip-top"
            data-tip="Minimum overall match score to accept an item. Lower = more items detected.">
        Minimum Similarity: <b>{(minSimilarity * 100).toFixed(0)}%</b>
      </span>
    </label>
    <input
      type="range"
      className="range range-xs"
      min={0.3}
      max={1}
      step={0.05}
      value={minSimilarity}
      onChange={(e) => setMinSimilarity(Number(e.target.value))}
    />
  </div>

  {/* Post-detection filtering */}
  <div className="mt-3 p-3 rounded-xl border border-blue-500">
    <div className="font-medium mb-2">Duplicate Removal</div>
    <div className="text-sm opacity-70 mb-3">Applied after detection to identify unique items</div>
    
    <label className="label">
      <span className="label-text tooltip tooltip-top"
            data-tip="Items with text similarity above this threshold are considered duplicates. Higher = stricter (fewer duplicates removed).">
        Duplicate Threshold: <b>{(duplicateSimilarityThreshold * 100).toFixed(0)}%</b>
      </span>
    </label>
    <input
      type="range"
      className="range range-xs range-primary"
      min={0.5}
      max={1}
      step={0.05}
      value={duplicateSimilarityThreshold}
      onChange={(e) => {
        setDuplicateSimilarityThreshold(Number(e.target.value));
        // Re-run deduplication when threshold changes
        if (detectedItems.length > 0) {
          // Trigger re-calculation
        }
      }}
    />
    <div className="text-xs opacity-60 mt-1">
      Items with ≥{(duplicateSimilarityThreshold * 100).toFixed(0)}% text similarity are treated as duplicates
    </div>
    
    {detectedItems.length > 0 && (
      <div className="text-sm mt-2">
        <span className="font-medium">{detectedItems.length}</span> total detections → 
        <span className="font-medium ml-1">{uniqueItems.length}</span> unique items
      </div>
    )}
  </div>
      </div>

      {/* Right: Results */}
      <div className="lg:col-span-1">
        <div className="flex items-center justify-between mb-2">
          <div className="font-semibold">Detection Results</div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1 cursor-pointer">
              <input
                type="checkbox"
                className="checkbox checkbox-sm"
                checked={ignoreDuplicates}
                onChange={(e) => setIgnoreDuplicates(e.target.checked)}
              />
              <span className="text-sm">Ignore duplicates</span>
            </label>
          </div>
        </div>
        
        <div className="rounded-xl border p-3 mb-3">
          <div className="flex justify-between text-sm mb-2">
            <span>Total detected: <b>{detectedItems.length}</b></span>
            <span>Unique items: <b>{uniqueItems.length}</b></span>
          </div>
          
          {listDisplayItems.length > 0 && (
            <div className="flex gap-2 mt-3">
              <button className="btn btn-success btn-sm flex-1" onClick={exportCSV}>
                Export CSV
              </button>
              <button className="btn btn-primary btn-sm flex-1" onClick={exportTXT}>
                Export TXT
              </button>
            </div>
          )}
        </div>

        {/* Items table */}
        <div className="w-full border-2 rounded-lg">
          <p className="text-lg text-center font-bold p-2">
            Detected Items {ignoreDuplicates && <span className="text-sm font-normal">(Unique)</span>}
          </p>
          <div className="max-h-[50vh] overflow-auto">
            <table className="w-full table table-sm">
              <thead className="sticky top-0 bg-base-300">
                <tr>
                  <th className="text-center w-12">#</th>
                  <th className="text-center w-16">Frame</th>
                  <th className="text-left">Content</th>
                  {!ignoreDuplicates && <th className="text-center w-16">Match</th>}
                </tr>
              </thead>
              <tbody>
                {listDisplayItems.length === 0 ? (
                  <tr>
                    <td colSpan={ignoreDuplicates ? 3 : 4} className="text-center opacity-60 py-4">
                      {templateLocked 
                        ? "Click 'Detect All Items' to find items"
                        : "Define and lock a template first"}
                    </td>
                  </tr>
                ) : (
                  listDisplayItems.map((item, idx) => (
                    <tr 
                      key={idx}
                      className={item.frameIndex === currentIndex ? 'bg-base-200' : ''}
                    >
                      <td className="text-center text-xs">
                        {ignoreDuplicates ? item.uniqueId : idx + 1}
                      </td>
                      <td className="text-center">
                        <button
                          className="btn btn-xs"
                          onClick={() => setSelectedFrame(item.frame)}
                        >
                          {item.frame}
                        </button>
                      </td>
                      <td className="text-xs">
                        {item.texts.map((text, i) => (
                          <div key={i} className="truncate" title={text}>
                            {text || <span className="opacity-40">(empty)</span>}
                          </div>
                        ))}
                      </td>
                      {!ignoreDuplicates && (
                        <td className="text-center text-xs">
                          {(item.similarity * 100).toFixed(0)}%
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Statistics */}
        {uniqueItems.length > 0 && (
          <div className="mt-3 p-3 rounded-xl border">
            <div className="font-medium mb-2">Statistics</div>
            <div className="text-sm space-y-1">
              <div>Frames processed: <b>{frames.length}</b></div>
              <div>Items detected: <b>{detectedItems.length}</b></div>
              <div>Unique items: <b>{uniqueItems.length}</b></div>
              <div>Duplicates found: <b>{detectedItems.length - uniqueItems.length}</b></div>
              <div>Avg items per frame: <b>
                {(detectedItems.length / frames.length).toFixed(1)}
              </b></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Corner handles
function renderHandle(pos, beginResize) {
  const size = 12;
  const half = size / 2;
  const style = {
    left: pos.includes("w") ? -half : undefined,
    right: pos.includes("e") ? -half : undefined,
    top: pos.includes("n") ? -half : undefined,
    bottom: pos.includes("s") ? -half : undefined,
    width: size,
    height: size,
    cursor: pos === "nw" || pos === "se" ? "nwse-resize" : "nesw-resize",
  };
  
  return (
    <div
      onMouseDown={(e) => beginResize(pos, e)}
      className="absolute bg-white border-2 border-purple-600 rounded-full"
      style={style}
      title="Drag to resize"
    />
  );
}